<template>
  <div class="wrapper">
    <div class="chat-form">
      <div
        id="chat-container"
        @wheel.passive="stopScroll = true"
        @touchmove.passive="stopScroll = true"
      >
        <div
          v-for="(bubble, key) in bubbles"
          :key="'bubble-' + key"
          :class="[
            'chat-bubble',
            bubble.role === 'assistant' ? '' : 'bubble-right',
          ]"
          v-html="bubble.content"
        ></div>
        <div v-show="loading" class="chat-bubble">
          <img src="../assets/loading.gif" class="loading-anim" />
        </div>
      </div>

      <div v-if="isYesOrNo" class="yes-or-no">
        <button class="btn-primary" @click="$emit('onYesOrNo', true)">
          {{ translation.yesText }}
        </button>
        <button class="btn-primary" @click="$emit('onYesOrNo', false)">
          {{ translation.noText }}
        </button>
      </div>
      <div v-if="choices" class="yes-or-no">
        <button
          v-for="(choice, idx) in choices"
          :key="'choice-' + idx"
          class="btn-primary"
          @click="$emit('clickChoice', idx)"
        >
          {{ choice }}
        </button>
      </div>
      <div v-else class="input-row">
        <textarea
          v-model="textInput"
          :disabled="inputDisabled"
          class="chat-input"
          :rows="5"
          type="text"
          :placeholder="
            inputDisabled
              ? translation.inputDisabledText
              : translation.inputText
          "
          @keypress="onKeyPress"
        />
        <button
          v-if="speechApiSupported"
          class="btn-primary mic"
          @click="startSpeechToText"
        >
          <img
            v-if="listening"
            src="../assets/pause_24dp_FFF_FILL1_wght400_GRAD0_opsz24.svg"
            style="height: 25px"
          />
          <img
            v-else
            src="../assets/mic_24dp_FFF_FILL1_wght400_GRAD0_opsz24.svg"
            style="height: 25px"
          />
        </button>
        <button
          class="btn-primary send-btn"
          :disabled="textInput.length < 5 || isThinking"
          @click="
            recognition && recognition.stop();
            send();
          "
        >
          {{ translation.sendText }}
        </button>
      </div>
      <div>
        <i class="small">{{ translation.disclaimerText }}</i>
      </div>
      <div>
        <i class="small"
          >{{ translation.contactText }} (<a
            href="mailto:galabau@jardinsuisse.ch?subject=JardinBot Feedback"
            >galabau@jardinsuisse.ch</a
          >).</i
        >
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ChatView",
  props: {
    inputDisabled: Boolean,
    isYesOrNo: { type: Boolean, default: false },
    choices: Array,
  },
  data() {
    return {
      textInput: "",
      isThinking: false,
      chatContainer: undefined,
      interval: undefined,
      stopScroll: false,
      listening: false,
      loading: false,
      speechApiSupported: "webkitSpeechRecognition" in window,
      language: "de",
      texts: {
        de: {
          welcomeText:
            "Hallo, ich bin der JardinBot. Wie kann ich dir heute weiterhelfen?",
          inputDisabledText:
            "Bitte beachten Sie die letzte Nachricht um fortzufahren...",
          inputText: "Geben Sie eine Nachricht ein...",
          sendText: "Senden",
          yesText: "Ja",
          noText: "Nein",
          disclaimerText:
            "JardinBot kann Fehler machen. Überprüfe wichtige Informationen.",
          contactText: "Melde uns Unstimmigkeiten",
        },
        en: {
          welcomeText: "Hi, I am JardinBot. How can I help you today?",
          inputDisabledText:
            "Please pay attention to the last message to continue...",
          inputText: "Type a message...",
          sendText: "Send",
          yesText: "Yes",
          noText: "No",
          disclaimerText:
            "JardinBot can make mistakes. Verify important information.",
          contactText: "Report any discrepancies to us",
        },
        fr: {
          welcomeText:
            "Bonjour, je suis JardinBot. Comment puis-je vous aider aujourd'hui?",
          inputDisabledText:
            "Veuillez prêter attention au dernier message pour continuer...",
          inputText: "Tapez un message...",
          sendText: "Envoyer",
          yesText: "Oui",
          noText: "Non",
          disclaimerText:
            "JardinBot peut faire des erreurs. Vérifie les informations importantes.",
          contactText: "Signale-nous les incohérences",
        },
        it: {
          welcomeText: "Ciao, sono JardinBot. Come posso aiutarti oggi?",
          inputDisabledText:
            "Si prega di prestare attenzione all'ultimo messaggio per continuare...",
          inputText: "Digita un messaggio...",
          sendText: "Invia",
          yesText: "Sì",
          noText: "No",
          disclaimerText:
            "JardinBot può commettere errori. Controlla le informazioni importanti.",
          contactText: "Segnalaci eventuali incongruenze",
        },
      },
    };
  },
  computed: {
    bubbles() {
      return [
        {
          content: this.translation.welcomeText,
          role: "assistant",
        },
        ...this.$store.state.allBubbles,
      ];
    },
    translation() {
      return this.texts[this.language] || this.texts.de;
    },
  },
  mounted() {
    this.checkAuthentication();
    this.detectLanguage();
  },
  methods: {
    scrollDown() {
      if (!this.stopScroll) {
        setTimeout(() => {
          this.chatContainer.scrollTo({
            top: this.chatContainer.scrollHeight,
            behavior: "smooth",
          });
        }, 20);
      }
    },
    onKeyPress(event) {
      if (event.key === "Enter") {
        event.preventDefault();
        if (!this.isThinking && this.textInput.length >= 5) {
          this.recognition && this.recognition.stop();
          this.send();
        }
      }
    },
    checkAuthentication() {
      const ALLOWED_ORIGINS = [
        "https://jardinsuisse.ch",
        "https://staging.jardin-suisse.dudagroup.com",
      ];
      window.addEventListener("message", (event) => {
        if (!ALLOWED_ORIGINS.includes(event.origin)) return;
        if ("authenticated" in event.data) {
          this.$store.commit("setAuthenticated", event.data.authenticated);
        }
      });
      window.parent.postMessage({ type: "NEED_AUTH_STATE" }, "*");
    },
    detectLanguage() {
      this.chatContainer = document.getElementById("chat-container");
      const language = window.location.pathname.split("/")[1];
      if (["de", "en", "fr", "it"].includes(language)) {
        this.language = language;
      }
    },
    send() {
      this.stopScroll = false;
      this.scrollDown();
      this.$store.commit("addBubble", {
        content: this.textInput,
        role: "user",
        streaming: false,
      });
      this.loading = true;
      this.textInput = "";
      let countOfChars = this.$store.state.allBubbles.reduce(
        (acc, bubble) => acc + bubble.content.length,
        0
      );

      // take last n bubbles that are under 10000 characters
      let newBubbles = [];
      if (countOfChars > 10000) {
        let bubbles = this.$store.state.allBubbles;
        let totalChars = 0;
        for (let i = bubbles.length - 1; i >= 0; i--) {
          if (totalChars + bubbles[i].content.length <= 10000) {
            newBubbles.unshift(bubbles[i]);
            totalChars += bubbles[i].content.length;
          } else {
            break;
          }
        }
      } else {
        newBubbles = this.$store.state.allBubbles;
      }

      fetch("/api/chat-stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          authenticated: this.$store.state.authenticated,
          messages: newBubbles,
        }),
      }).then((response) => {
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        this.$store.commit("addBubble", {
          content: "",
          role: "assistant",
          streaming: true,
        });
        const index = this.$store.state.allBubbles.length - 1;

        const readStream = () => {
          reader.read().then(({ done, value }) => {
            this.scrollDown();
            if (done) {
              // Stream complete
              return;
            }

            const chunk = decoder.decode(value, { stream: true });
            this.$store.state.allBubbles[index].content += chunk;

            readStream();
          });
        };

        this.loading = false;
        readStream();
      });
    },
    startSpeechToText() {
      if (this.listening) {
        this.recognition.stop();
        return;
      }
      if (this.inputDisabled) return;
      this.recognition = new window.webkitSpeechRecognition();
      this.recognition.onstart = () => {
        this.listening = true;
      };
      this.recognition.onresult = (e) => {
        const lastIdx = e.results.length - 1;
        var transcript = e.results[lastIdx][0].transcript;
        this.textInput += transcript + "";
      };
      this.recognition.onend = () => {
        this.listening = false;
      };
      this.recognition.continuous = true;

      this.recognition.start();
    },
  },
};
</script>

<style scoped>
/* .wrapper {
  user-select: none;
} */
.chat-input {
  padding: 6px 12px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  appearance: none;
  border-radius: 4px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  width: 70%;
  :focus {
    color: #212529;
    background-color: #fff;
    border-color: #86b7fe;
    outline: 0;
    box-shadow: 0 0 0 0.25rem rgb(13 110 253 / 25%);
  }
}
.chat-form {
  width: 70vw;
  height: 90vh;
  margin: 0 auto;
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: end;
}
#chat-container {
  width: 100%;
  max-height: 90%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}
.close-icon {
  width: 50px;
  height: 50px;
  position: absolute;
  top: 5vh;
  right: 5vw;
}
.close-icon:hover {
  cursor: pointer;
}
.input-row {
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
}
.send-btn {
  height: 36px;
  margin-left: 4px;
  border-radius: 4px;
}
textarea {
  resize: none;
}
.loading-anim {
  height: 18px;
}
@media (max-width: 600px) {
  .chat-form {
    width: 90vw;
  }
  .close-icon {
    top: 0;
    right: 0;
  }
}
.mic {
  border-radius: 50%;
  padding: 8px;
  height: 40px;
  width: 40px;
  background-color: #e0e0e0;
}
.chat-bubble {
  position: relative;
  width: fit-content;
  max-width: 75%;
  word-wrap: break-word;
  text-align: left;

  margin: 0 10px 20px;
  background-color: #fff;
  padding: 1.125em 1.5em;
  font-size: 1.25em;
  border-radius: 1rem;
  box-shadow: 0 0.125rem 0.2rem rgba(0, 0, 0, 0.3);
}
.chat-bubble::before {
  content: "";
  position: absolute;
  width: 0;
  height: 0;
  top: 100%;
  left: 24px;
  border: 12px solid transparent;
  border-bottom: none;

  border-top-color: #fff;
  filter: drop-shadow(0.05rem 0.2rem 0.1rem rgba(0, 0, 0, 0.1));
}
.btn-primary {
  display: block;
  color: #fff;
  background-color: #019349;
  align-content: center;
  border: none;
}
.btn-primary:hover {
  cursor: pointer;
}
.yes-or-no {
  display: flex;
  justify-content: space-around;
  flex-direction: row;
  width: 100%;
  flex-wrap: wrap;
}
.yes-or-no > button {
  min-height: 36px;
  padding: 10px 40px;
  border-radius: 4px;
  background-color: #b0bec5;
  margin-bottom: 20px;
}
.bubble-right {
  align-self: flex-end;
  background-color: #a5d6a7;
}
.bubble-right::before {
  right: 24px;
  left: auto;
  border-top-color: #a5d6a7;
}
.small {
  font-size: 0.8em;
}
</style>
